import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { connect } from "react-redux";
import { Dispatch } from "redux";

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { ObjectUtils } from 'src/core/utils/object';
import { ContextService } from 'src/core/services/context.service';
import { FormatService } from 'src/core/services/format.service';
import { ConfigurationService } from 'src/core/services/configuration.service';
import { AllotmentFormView } from './allotment-form.view';
import { AllotmentDataService } from './allotment-form.data';
import { actionCreators } from "src/store/products/product-form-nav-actions";

interface PropsFromState {
	otherLoadedEntities: ReadonlyArray<Readonly<{
		entity: ProductFormItemType
	}>>;
}

interface PropsFromDispatch {
	clearStore: () => any;
}

interface AllotmentFormParams {
	id: string;
}

interface Props extends ControllerProps<AllotmentFormParams> {
}

@observer
class AllotmentFormController extends React.Component<Props & PropsFromDispatch & PropsFromState, {}> {
	@observable
	protected dataService: AllotmentDataService;

	@resolve
	protected contextService: ContextService;

	@resolve
	protected formatService: FormatService;

	@resolve
	protected toast: ToastNotifications;

	@resolve
	protected configurationService: ConfigurationService;

	@observable
	mode: ControlMode = 'display';

	@computed
	protected get title() {
		if (this.dataService && this.dataService.item && this.dataService.item.name) {
			return `Allotment ${this.dataService.item.name}`;
		}
		return '';
	}

	@autobind
	protected onModeChanged(mode: ControlMode) {
		this.mode = mode;
	}

	@autobind
	public async save() {
		const item = this.dataService.item;
		const errors = await item.validate(true);
		if (errors.length == 0) {
			this.props.setBusy(true);
			this.prepareForSave(this.dataService.item);
			this.dataService.save()
				.then(() => {
					this.props.setBusy(false);
					this.onModeChanged('display');
					this.toast.showDataSaved();
					if (this.props.match.params.id === "new") {
						this.props.history.replace(`/products/allotment/${this.dataService.item.entity.id}`);
					}
				})
				.catch((error) => {
					this.props.setBusy(false);
					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
				});
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	}

	@autobind
	public cancel(): void {
		this.props.history.push('/products/allotment');
	}

	componentDidMount() {
		this.dataService = AllotmentDataService.make(this.contextService);
		this.props.setBusy(true);
		if (this.props.match.params.id == 'new') {
			Promise.all([this.configurationService.current(), this.dataService.add()])
				.then((res) => {
					this.mode = 'edit';
					this.props.setBusy(false);
				})
				.catch((error) => {
					this.props.setBusy(false);
					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
				});
		} else {
			this.dataService.addFilter('id', 'eq', this.props.match.params.id);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;
			Promise.all([this.configurationService.current(), this.dataService.load(query => query)])
				.then((res) => {
					this.props.setBusy(false);
				})
				.catch((error) => {
					this.props.setBusy(false);
					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
				});
		}
	}

	render() {
		if (this.dataService == null || this.dataService.item == null) {
			return null;
		}

		return (
			<AllotmentFormView
				toast={this.toast}
				formatService={this.formatService}
				title={this.title}
				mode={this.mode}
				onModeChanged={this.onModeChanged}
				cancel={this.cancel}
				save={this.save}
				contextService={this.contextService}
				model={this.dataService.item}
				setBusy={this.props.setBusy}
				defaultLanguaje={this.configurationService.configuration ? this.configurationService.configuration.defaultLanguage : ""}
				fromContract={false}
			/>
		);
	}

	private prepareForSave(allotment: AllotmentItem): void {
		const { otherLoadedEntities } = this.props;
		const inStore: AllotmentItem = otherLoadedEntities.find(e => e.entity == allotment.entity) as any;

		if (inStore.allotmentRanges) {
			for (let prod of inStore.allotmentRanges) {

				const inStore: AllotmentRangeItem = otherLoadedEntities.find(e => e.entity == prod.entity) as any;

				if (inStore.allotmentRangeDailies) {
					for (const item of inStore.allotmentRangeDailies) {
						if (prod.id < 0) {
							item.entity.allotmentRange = prod.entity;
							item.allotmentRangeId = undefined;
						}
					}
				}

				if (allotment.id < 0) {
					prod.entity.allotment = allotment.entity;
					prod.allotmentId = undefined;
				}
			}
		}
	}
}

const mapStateToProps = (state: IApplicationState): PropsFromState => ({
	otherLoadedEntities: state.otherLoadedEntities,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	clearStore: () => dispatch(actionCreators.clearStore()),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(AllotmentFormController);
export { connected as AllotmentFormController };



// WEBPACK FOOTER //
// ./src/app/products/allotment/form/allotment-form.controller.tsx